import * as React from 'react';
import { purchaseOrderStyles } from './purchase-orders.styles';
import Paginator from '../common/paginator';
import {
    Input,
    DatePicker,
    message,
    Select,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { HocOptions } from '../common/generic-hoc.types';
import { StylesProps } from '../../theme/jss-types';
import { purchaseOrderColumns } from './purchase-orders.constants';
import { ReduxStore } from '../../reducers/redux.types';
import { bindActionCreators } from 'redux';
import {
    setPagination,
} from '../../actions/generic-action';
import { Buckets, NextOrPrev, Pagination } from '../../library/Types';
import GenericHoc from '../common/generic-hoc';
import { RouteChildrenProps } from 'react-router';
import { loadOrders } from '../../actions/purchase-actions';
import { useDebounce } from 'hooks/use-debounce';
import { Master } from 'types/master-data-types';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment';
import PurchaseOrderDetails from './purchase-order-details';
import { getPurchaseOrderDetails } from 'network/purchase.api';
import FixedHeaderTable from 'components/common/FixedHeaderTable/FixedHeaderTable';
import { getCurrentCustomer } from 'components/common/utils';

interface Props
    extends StylesProps<ReturnType<typeof purchaseOrderStyles>>,
    RouteChildrenProps {
    filters: Record<string, any>;
    loading: boolean;
    loadorders: (searchString: string, dateRange?: [Moment, Moment], childCustomerCode?: string) => void;
    pagination: Pagination,
    ordersList: any,
    setpagination: (bucketId: Buckets.PURCHASE_ORDER, newPagination: Partial<Pagination>) => void;
    master: Master,
}

const {
    useState,
    useEffect,
} = React;

const PurchaseOrdersPage = (props: Props) => {
    const [searchString, setSearchString] = useState<string>('');
    const [toggle, setToggle] = useState<boolean>(true);
    const [dateRange, setDateRange] = React.useState<[Moment, Moment]>([
        moment().subtract(7, 'days'),
        moment(),
    ]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [showDetailsModal, setShowDetailsModal] = useState<boolean>(false);
    const [selectedOrderDetails, setSelectedOrderDetails] = useState<any>({});
    const [childClients, setChildClients] = useState<any[]>([]);
    const [selectedChild, setSelectedChild] = useState<any>({});

    const { t } = useTranslation();

    const {
        classes,
        loadorders,
        setpagination,
        loading,
        pagination,
        ordersList,
        master,
    } = props;

    const debounceTimeSearchString = useDebounce(searchString);
    const loadAccounts = async () => {
        setIsLoading(true);
        const result = getCurrentCustomer(master?.children_list);
        setChildClients(result || []);
        const customer = result?.length ? result[0] : undefined;
        setSelectedChild(customer);
        setIsLoading(false);
    };

    useEffect(() => {
        loadAccounts();
    }, []);

    useEffect(() => {
        loadorders(searchString, dateRange, selectedChild?.customer_code);
    }, [debounceTimeSearchString, toggle, dateRange, selectedChild]);

    const renderText = (text: string | number) => {
        return (
            <div
                className={classes.cellValue}
            >
                {text}
            </div>
        );
    };

    const getOrderDetails = async (id: string) => {
        setIsLoading(true);
        const response = await getPurchaseOrderDetails({
            referenceNumber: id,
        });
        if (response.isSuccess) {
            setSelectedOrderDetails(response.data);
            setShowDetailsModal(true);
        } else {
            message.error(response.errorMessage);
        }
        setIsLoading(false);
    };

    const renderReferenceNumber = (text: string) => {
        return (
            <div
                className={classes.cellValue}
                style={{ cursor: 'pointer' }}
                onClick={() => getOrderDetails(text)}
            >
                {text}
            </div>
        );
    };

    const renderColumn = (text: any, column: string) => {
        if (typeof text === 'boolean') {
            return renderText(text ? 'Yes' : 'No');
        }
        if (!text || text === null) {
            return <div className={classes.cellNa}>—</div>;
        }
        if (column === 'reference_number') {
            return renderReferenceNumber(text);
        }
        return renderText(text);
    };

    const getColumns = (): any[] => {
        const columns: any = Object.keys(purchaseOrderColumns).map((column: string) => {
            return {
                key: column,
                title: purchaseOrderColumns[column],
                dataIndex: column,
                width: 80,
                ellipsis: true,
                render: (text: string) => renderColumn(text, column),
            };
        });
        return columns;
    };

    const renderTable = () => {
        return (
            <FixedHeaderTable
                bordered={false}
                pagination={false}
                loading={loading || isLoading}
                rowKey={(row) => row.id}
                columns={getColumns()}
                style={{
                    height: 'calc(100% - 80px)',
                    background: '#ffffff',
                }}
                scroll={{
                    y: 'calc(100% - 110px)',
                }}
                dataSource={ordersList?.data || []}
            />
        );
    };

    const handleSearchQuery = (e: any) => {
        const newPagination = {
            ...pagination,
            currentPageNumber: 1,
        };
        setpagination(Buckets.PURCHASE_ORDER, newPagination);
        setSearchString(e.target.value);
    };


    const handleDateChange = (value: any) => {
        if (!(value && value[0] && value[1])) return;
        const newFilters: [Moment, Moment] = [
            ...dateRange,
        ];
        if (value[1].isSame(dateRange[1])) {
            if (moment(dateRange[1]).diff(value[0], 'days') > 30) {
                const endDate = moment(value[0]).add(30, 'd');
                newFilters[1] = endDate;
                newFilters[0] = moment(value[0]);
            } else {
                newFilters[1] = moment(value[1]);
                newFilters[0] = moment(value[0]);
            }
        } else if (moment(value[1]).diff(value[0], 'days') > 30) {
            const startDate = moment(value[1]).subtract(30, 'd');
            newFilters[0] = startDate;
            newFilters[1] = moment(value[1]);
        } else {
            newFilters[1] = moment(value[1]);
            newFilters[0] = moment(value[0]);
        }
        setDateRange(newFilters);
    };

    const hideDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedOrderDetails({});
    };

    const renderRangePicker = () => {
        return (
            <DatePicker.RangePicker
                dropdownClassName={classes.dateRange}
                allowEmpty={[false, false]}
                allowClear={false}
                value={dateRange}
                onChange={handleDateChange}
            />
        );
    };

    const renderDateFilter = () => {
        return (
            <div className={classes.dateFilter}>
                {renderRangePicker()}
            </div>
        );
    };

    const renderSearch = () => {
        return (
            <Input
                className={classes.searchInput}
                type="search"
                value={searchString}
                onChange={(e: any) => handleSearchQuery(e)}
                placeholder={t('Search')}
                suffix={<SearchOutlined />}
            />
        );
    };

    const renderCustomer = () => {
        if (!childClients.length) {
            return null;
        }
        return (
            <div className={classes.filter}>
                <span className={classes.filterText}>
                    {t('customer')}
                </span>
                <Select
                    className={classes.selectFilter}
                    value={selectedChild?.customer_code}
                    onChange={(value: any) => {
                        const customer = childClients.find((client) => client.customer_code === value);
                        setSelectedChild(customer);
                    }}
                    options={childClients.map((client) => ({
                        label: client.customer_name,
                        value: client.customer_code,
                    }))}
                    placeholder="Select a customer"
                    dropdownMatchSelectWidth={false}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode}
                />
            </div>
        );
    };

    const renderLeftFilters = () => {
        return (
            <div className={classes.leftFilters}>
                {renderCustomer()}
                {renderSearch()}
            </div>
        );
    };


    const handlePagination = (nextOrPrev: NextOrPrev) => {
        const { currentPageNumber } = pagination;
        let newPageNumber = currentPageNumber;
        if (nextOrPrev === 'first') {
            newPageNumber = 1;
        } else if (nextOrPrev === 'next') {
            newPageNumber = currentPageNumber + 1;
        } else {
            newPageNumber = currentPageNumber - 1;
        }
        const newPagination = {
            ...pagination,
            nextOrPrev,
            currentPageNumber: newPageNumber,
        };
        setpagination(Buckets.PURCHASE_ORDER, newPagination);
        setToggle(!toggle);
    };

    const renderPaginator = () => {
        return (
            <div style={{ direction: 'ltr' }}>
                <Paginator
                    currentPageNumber={pagination.currentPageNumber}
                    isNextPresent={ordersList?.isNextPresent}
                    onNextClick={() => handlePagination('next')}
                    onPrevClick={() => handlePagination('prev')}
                />
            </div>
        );
    };

    const renderRightFilters = () => {
        return (
            <div className={classes.rightFilters}>
                {renderDateFilter()}
            </div>
        );
    };

    const renderExtraFilters = () => {
        return (
            <div className={classes.extraFilters}>
                {renderLeftFilters()}
                {renderRightFilters()}
            </div>
        );
    };

    const renderFilters = () => {
        return (
            <div className={classes.filters}>
                {renderExtraFilters()}
            </div>
        );
    };

    const renderFooter = () => {
        return (
            <div className={classes.footer}>
                {renderPaginator()}
            </div>
        );
    };

    return (
        <div className={classes.main}>
            {renderFilters()}
            <>
                {renderTable()}
                {renderFooter()}
            </>
            {showDetailsModal && (
                <PurchaseOrderDetails
                    onClose={hideDetailsModal}
                    data={selectedOrderDetails}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => {
    const { generic } = state;
    const { PURCHASE_ORDER } = generic;
    const {
        loading,
        data,
        pagination,
    } = PURCHASE_ORDER;
    return {
        loading,
        ordersList: data,
        pagination,
    };
};

const mapDispatchToProps = (dispatch: any) => {
    const actions = {
        loadorders: loadOrders,
        setpagination: setPagination,
    };
    return bindActionCreators(actions, dispatch);
};

const hocConfig: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: purchaseOrderStyles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
        mapDispatchToProps,
    },
    connectRouter: true,
    connectTranslession: true,
};

const PurchaseOrders = GenericHoc(hocConfig)(PurchaseOrdersPage);
export default PurchaseOrders;
